import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formsAPI } from '../../utils/crmApi';
import { useBranding } from '../../contexts/BrandingContext';

const Card = ({ title, value, hint, trend }) => (
  <div className="bg-card border rounded p-4">
    <div className="text-xs text-muted-foreground">{title}</div>
    <div className="text-2xl font-semibold text-foreground">{value}</div>
    {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    {trend && (
      <div className={`text-xs mt-1 ${trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
        {trend > 0 && '↑'} {trend < 0 && '↓'} {Math.abs(trend)}%
      </div>
    )}
  </div>
);

const FormAnalytics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]);
  const [form, setForm] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const primaryColor = branding?.primaryColor || '#3b82f6';

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        // Load form to get page structure
        const allForms = await formsAPI.getAll();
        const foundForm = ((await allForms).data?.data || []).find(f => f._id === id);
        setForm(foundForm || null);
        
        // Load submissions
        const { data: subsData } = await formsAPI.getSubmissions(id);
        setSubs(subsData.data || []);
        
        // Load analytics (views/starts)
        try {
          const { data: analyticsData } = await formsAPI.getAnalytics(id, { days: 30 });
          setAnalytics(analyticsData.data || null);
        } catch (e) {
          console.error('Failed to load analytics:', e);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const metrics = useMemo(() => {
    const total = subs.length;
    const completed = subs.filter(s => s.status !== 'abandoned').length;
    const abandoned = total - completed;
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
    const avgSec = completed ? Math.round(subs.filter(s => s.status !== 'abandoned').reduce((a, s) => a + (s.meta?.durationSec || 0), 0) / completed) : 0;
    const avgMin = Math.floor(avgSec / 60);
    const avgSecRem = avgSec % 60;
    const reviewed = subs.filter(s => s.status === 'reviewed').length;
    const converted = subs.filter(s => s.status === 'converted').length;
    const sources = subs.reduce((acc, s) => { const src = s.meta?.source || 'unknown'; acc[src] = (acc[src] || 0) + 1; return acc; }, {});
    
    // Page drop-off analysis
    const pageStats = [];
    if (form?.pages) {
      form.pages.forEach((page, pageIdx) => {
        const pageStarts = subs.filter(s => {
          // Estimate if user reached this page (heuristic: if answers exist for fields on earlier pages)
          const prevPages = form.pages.slice(0, pageIdx);
          const prevFields = prevPages.flatMap(p => (p.elements || []).filter(e => e.key && e.type !== 'page_break' && e.type !== 'title' && e.type !== 'description').map(e => e.key));
          return prevFields.some(key => s.answers?.[key] !== undefined && s.answers?.[key] !== null && s.answers?.[key] !== '');
        }).length;
        const pageCompletions = subs.filter(s => {
          const pageFields = (page.elements || []).filter(e => e.key && e.type !== 'page_break' && e.type !== 'title' && e.type !== 'description').map(e => e.key);
          return pageFields.some(key => s.answers?.[key] !== undefined && s.answers?.[key] !== null && s.answers?.[key] !== '');
        }).length;
        const dropOff = pageStarts > 0 ? Math.round(((pageStarts - pageCompletions) / pageStarts) * 100) : 0;
        pageStats.push({ pageIdx, title: page.title || `Page ${pageIdx + 1}`, starts: pageStarts, completions: pageCompletions, dropOff });
      });
    }
    
    return { total, completed, abandoned, completionRate, avgSec, avgMin, avgSecRem, reviewed, converted, sources, pageStats };
  }, [subs, form]);

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Form Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">{form?.name || 'Form'}</p>
        </div>
        <div className="flex gap-2">
          <button className="px-3 py-2 border rounded hover:bg-muted" onClick={() => navigate(`/forms/${id}/responses`)}>View responses</button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {analytics ? (
          <>
            <Card title="Total views" value={analytics.views || 0} hint={`${analytics.viewToStartRate || 0}% started`} />
            <Card title="Total starts" value={analytics.starts || 0} hint={`${analytics.conversionRate || 0}% submitted`} />
            <Card title="Submissions" value={analytics.submissions || 0} hint={`${metrics.completed} completed`} />
            <Card title="Avg time" value={metrics.avgSec > 0 ? `${metrics.avgMin}m ${metrics.avgSecRem}s` : '-'} hint="For completed forms" />
          </>
        ) : (
          <>
            <Card title="Total submissions" value={metrics.total} hint={`${metrics.completed} completed`} />
            <Card title="Completion rate" value={`${metrics.completionRate}%`} hint={`${metrics.abandoned} abandoned`} />
            <Card title="Avg time to complete" value={metrics.avgSec > 0 ? `${metrics.avgMin}m ${metrics.avgSecRem}s` : '-'} hint="For completed forms" />
            <Card title="Reviewed" value={metrics.reviewed} hint={`${metrics.converted} converted`} />
          </>
        )}
      </div>
      
      {/* Conversion Funnel */}
      {analytics && (
        <div className="bg-card border rounded p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Conversion Funnel</h2>
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-foreground">Views</span>
                <span className="text-muted-foreground">{analytics.views}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-8">
                <div className="h-8 rounded-full flex items-center justify-center text-white text-xs font-medium" style={{ width: '100%', backgroundColor: primaryColor }}>
                  100%
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-foreground">Started filling</span>
                <span className="text-muted-foreground">
                  {analytics.starts} 
                  {analytics.views > 0 ? ` (${analytics.viewToStartRate}% of views)` : ' (0 views)'}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-8 relative overflow-hidden">
                <div 
                  className="h-8 rounded-full flex items-center justify-center text-white text-xs font-medium transition-all" 
                  style={{ 
                    width: `${Math.min(analytics.viewToStartRate || 0, 100)}%`,
                    maxWidth: '100%',
                    backgroundColor: primaryColor 
                  }}
                >
                  {analytics.viewToStartRate}%
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-foreground">Submitted</span>
                <span className="text-muted-foreground">
                  {analytics.submissions} 
                  {analytics.starts > 0 ? ` (${analytics.conversionRate}% of starts)` : ' (0 starts)'}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-8 relative overflow-hidden">
                <div 
                  className="bg-green-500 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium transition-all" 
                  style={{ 
                    width: `${Math.min(analytics.conversionRate || 0, 100)}%`,
                    maxWidth: '100%'
                  }}
                >
                  {analytics.conversionRate}%
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Completion Funnel */}
      {metrics.pageStats.length > 1 && (
        <div className="bg-card border rounded p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Completion Funnel</h2>
          <div className="space-y-3">
            {metrics.pageStats.map((stat, idx) => {
              const prevStat = idx > 0 ? metrics.pageStats[idx - 1] : null;
              const prevCompletions = prevStat?.completions || metrics.total;
              const conversion = prevCompletions > 0 ? Math.round((stat.completions / prevCompletions) * 100) : 0;
              const width = metrics.total > 0 ? Math.round((stat.completions / metrics.total) * 100) : 0;
              
              return (
                <div key={idx} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-foreground">{stat.title}</span>
                    <span className="text-muted-foreground">{stat.completions} / {prevCompletions} ({conversion}%)</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-6 relative overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${idx === metrics.pageStats.length - 1 ? 'bg-green-500' : ''}`}
                      style={{
                        ...(idx !== metrics.pageStats.length - 1 ? { backgroundColor: primaryColor } : {}),
                        width: `${Math.min(width, 100)}%`,
                        maxWidth: '100%'
                      }}
                    />
                    {stat.dropOff > 0 && (
                      <div 
                        className="absolute right-0 top-0 bottom-0 bg-red-200" 
                        style={{ 
                          width: `${Math.min(stat.dropOff, 100)}%`,
                          maxWidth: '100%'
                        }} 
                      />
                    )}
                  </div>
                  {stat.dropOff > 0 && (
                    <div className="text-xs text-red-600">⚠️ {stat.dropOff}% drop-off</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Page Drop-off Details */}
      {metrics.pageStats.length > 0 && (
        <div className="bg-card border rounded p-4">
          <div className="text-sm font-medium text-foreground mb-3">Page Drop-off Analysis</div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Page</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Started</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Completed</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Drop-off</th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-gray-200">
                {metrics.pageStats.map((stat, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-2 text-sm text-foreground">{stat.title}</td>
                    <td className="px-4 py-2 text-sm text-foreground">{stat.starts}</td>
                    <td className="px-4 py-2 text-sm text-foreground">{stat.completions}</td>
                    <td className="px-4 py-2 text-sm">
                      <span className={stat.dropOff > 30 ? 'text-red-600 font-medium' : stat.dropOff > 10 ? 'text-yellow-600' : 'text-muted-foreground'}>
                        {stat.dropOff}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Source Breakdown */}
      <div className="bg-card border rounded p-4">
        <div className="text-sm font-medium text-foreground mb-2">Source breakdown</div>
        <div className="text-sm text-foreground space-y-1">
          {Object.keys(metrics.sources).length === 0 && <div className="text-muted-foreground">No data.</div>}
          {Object.entries(metrics.sources).map(([k, v]) => {
            const pct = metrics.total > 0 ? Math.round((v / metrics.total) * 100) : 0;
            return (
              <div key={k} className="flex items-center justify-between py-1">
                <span className="capitalize">{k}</span>
                <div className="flex items-center gap-3">
                  <div className="w-32 bg-muted rounded-full h-2">
                    <div className="h-2 rounded-full" style={{ width: `${pct}%`, backgroundColor: primaryColor }} />
                  </div>
                  <span className="w-16 text-right">{v} ({pct}%)</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FormAnalytics;
